import { createSlice, createSelector, createAction, PayloadAction, CaseReducer } from '@reduxjs/toolkit';

// Types
export interface Sample {
    parent_sample_id?: number | null;
    sample_id: number;
    subgroup_id?: string | null;
    use_parent_info?: boolean;
    [key: string]: any; // For any extra fields
}

export interface Group {
    id: number;
    samples: Sample[];
    status: string | null;
    purpose: string;
    vendor: string | null;
    assay: string | null;
    creation_date: string;
    eta: string | null;
    experiment_id?: string | null;
    name: string;
    remoteId?: string | number;
    [key: string]: any; // For any extra fields
}

export interface CloudGroup extends Group {
}

export interface GroupState {
    groups: Record<number, Group>;
    cloudGroups: Record<number, CloudGroup>;
    activeGroupId: number | null;
    editedGroups?: number[];
}

const initialState: GroupState = {
    groups: {},
    cloudGroups: {},
    activeGroupId: null,
};

export const updateMultipleSubgroupIds = createAction(
    'group/updateMultipleSubgroupIds',
    ({ indices, subgroupId }: { indices: number[]; subgroupId: string }) => ({
        payload: { indices, subgroupId }
    })
);

export const groupSlice = createSlice({
    name: 'group',
    initialState,
    reducers: {
        // Group management actions
        createNewGroup: (state, action: PayloadAction<{ groupId: number; groupName: string }>) => {
            const { groupId, groupName } = action.payload;
            if (!state.groups[groupId]) {
                state.groups[groupId] = {
                    id: groupId,
                    samples: [],
                    status: null,
                    purpose: "",
                    vendor: null,  // Initialize as null for consistency with status
                    assay: null,   // Initialize as null for consistency with status
                    creation_date: new Date().toISOString().slice(0, 10),
                    name: groupName || `Group ${Object.keys(state.groups).length + 1}`,
                    eta: null,
                    experiment_id: "",
                };
            }

            // Set as active group if no active group is set
            if (!state.activeGroupId) {
                state.activeGroupId = groupId;
            }
        },
        setActiveGroup: (state, action: PayloadAction<number>) => {
            if (state.groups[action.payload]) {
                state.activeGroupId = action.payload;
            }
        },
        deleteGroup: (state, action: PayloadAction<number>) => {
            if (state.groups[action.payload]) {
                delete state.groups[action.payload];
                if (state.activeGroupId === action.payload) {
                    // Set activeGroupId to another group's ID if available, or null if no groups
                    state.activeGroupId = Object.keys(state.groups).length > 0 ?
                        Number(Object.keys(state.groups)[0]) : null;
                }
            }
        },
        renameGroup: (state, action: PayloadAction<{ groupId: number; newName: string }>) => {
            const { groupId, newName } = action.payload;
            if (state.groups[groupId]) {
                state.groups[groupId].name = newName;
            }
        },
        setGroups: (state, action: PayloadAction<Group[]>) => {
            // Add all remote groups to local state
            const remoteGroups = action.payload;
            remoteGroups.forEach((group) => {
                const groupId = group.id;
                if (!Object.values(state.groups).some((g) => g.id === group.id)) {
                    state.groups[groupId] = {
                        id: groupId,
                        samples: group.samples || [],
                        status: group.status,
                        purpose: group.purpose || "",
                        vendor: group.vendor || null,  // Change default to null for consistency
                        assay: group.assay || null,    // Change default to null for consistency
                        creation_date: group.creation_date || new Date().toISOString().slice(0, 10),
                        eta: group.eta || null,
                        name: group.name,
                        remoteId: group.remoteId || group["id"]
                    };
                } else {
                    // If group exists, preserve local samples
                    state.groups[groupId] = {
                        ...state.groups[groupId],
                        ...group,
                        samples: state.groups[groupId].samples, // preserve local samples
                    };
                }
            });

            // Set active group if none is set
            if (!state.activeGroupId && Object.keys(state.groups).length > 0) {
                state.activeGroupId = Number(Object.keys(state.groups)[0]);
            }
        },

        // Add this new action to completely replace local groups with cloud groups
        // Replace the replaceGroupsFromCloud reducer with this implementation
        replaceGroupsFromCloud: (state, action: PayloadAction<CloudGroup[]>) => {
            // Remember the active group name before clearing state
            const activeGroupId = state.activeGroupId;

            // Keep a reference to existing groups BEFORE clearing state
            const existingGroups = { ...state.groups };

            // Now start with clean slate
            state.groups = {};

            // Add all remote groups to local state
            const remoteGroups = action.payload;
            remoteGroups.forEach((group) => {
                const groupId = group.id;

                // Check if this group existed before with local modifications
                const existingGroup = existingGroups[groupId];

                // Use cloud samples/sample as the base (API sometimes returns 'sample' instead of 'samples')
                const cloudSamples: Sample[] = group.samples || group.sample || [];

                state.groups[groupId] = {
                    ...group,
                    samples: existingGroup && existingGroup.samples ? existingGroup.samples : cloudSamples,
                    status: group.status,
                    purpose: group.purpose || "",
                    vendor: group.vendor || null,
                    assay: group.assay || null,
                    creation_date: (group.creation_date && (typeof group.creation_date === 'object' && 'toISOString' in group.creation_date))
                        ? (group.creation_date as Date).toISOString().slice(0, 10)
                        : typeof group.creation_date === 'string' && group.creation_date.includes('T')
                            ? group.creation_date.split('T')[0]
                            : group.creation_date || new Date().toISOString().slice(0, 10),
                    eta: group.eta || null,
                    experiment_id: group.experiment_id || null,
                    name: group.name,
                    remoteId: group.id
                };
            });

            // Restore active group selection by finding the group with the same name
            if (activeGroupId) {
                const newGroupId = Object.keys(state.groups).map(Number).find(id =>
                    state.groups[id].id === activeGroupId
                );

                if (newGroupId !== undefined) {
                    // Found the same group by name - keep it selected
                    state.activeGroupId = newGroupId;
                } else {
                    // Only fall back to first group if active group no longer exists
                    state.activeGroupId = Object.keys(state.groups).length > 0 ?
                        Number(Object.keys(state.groups)[0]) : null;
                }
            }
        },

        // Add a new action to revert a single group to its cloud state
        replaceGroupFromCloud: (state, action: PayloadAction<number>) => {
            const groupId = action.payload;

            // Ensure the group exists in cloud and local state
            if (!state.cloudGroups[groupId] || !Object.values(state.groups).some((g) => g.id === groupId)) {
                return;
            }

            // Get the cloud version of the group
            const cloudGroup = state.cloudGroups[groupId];

            // Find local group ID by id
            const localGroupId = Object.keys(state.groups).map(Number).find(
                (id) => state.groups[id].id === groupId
            );

            if (localGroupId === undefined) return;

            // Replace local group with cloud version
            state.groups[localGroupId] = {
                ...cloudGroup,
                // Preserve the local ID to avoid reference issues
                remoteId: cloudGroup.remoteId || state.groups[localGroupId].remoteId
            };
        },

        // Update cloud groups state
        updateCloudGroups: (state, action: PayloadAction<CloudGroup[]>) => {
            state.cloudGroups = {};
            const cloudGroups = action.payload;

            cloudGroups.forEach((group) => {
                if (!group || !group.id) return;

                state.cloudGroups[group.id] = {
                    ...group,
                    samples: group.samples || group.sample || [],
                    status: group.status,
                    purpose: group.purpose || "",
                    vendor: group.vendor || null,  // Change default to null for consistency
                    assay: group.assay || null,    // Change default to null for consistency
                    creation_date: (group.creation_date && (typeof group.creation_date === 'object' && 'toISOString' in group.creation_date))
                        ? (group.creation_date as Date).toISOString().slice(0, 10)
                        : typeof group.creation_date === 'string' && group.creation_date.includes('T')
                            ? group.creation_date.split('T')[0]
                            : group.creation_date || new Date().toISOString().slice(0, 10),
                    eta: group.eta || null,
                    name: group.name,
                    remoteId: group.id,
                    experiment_id: group.experiment_id || null
                };
            });
        },

        // Sample actions (modified to work with the active group)
        addSample: (state, action: PayloadAction<{ parent_sample_id?: number | null; sample_id: number; subgroup_id?: string | null }>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                const { parent_sample_id, sample_id, subgroup_id } = action.payload;

                // If this is a derivative sample (has parent_sample_id), always add it
                // If it's not a derivative, check if it already exists in the group
                const isDerivedSample = parent_sample_id !== null && parent_sample_id !== undefined;
                const existingIndex = state.groups[state.activeGroupId].samples.findIndex(
                    (sample: Sample) => sample.sample_id === sample_id
                );

                // Only add if it's a derived sample or it doesn't already exist in the group
                if (isDerivedSample || existingIndex === -1) {
                    state.groups[state.activeGroupId].samples.push({
                        parent_sample_id,
                        sample_id,
                        subgroup_id: subgroup_id || null,
                    });
                }
            }
        },
        addSamplesInBatch: (state, action: PayloadAction<Sample[]>) => {
            if (!state.activeGroupId || !state.groups[state.activeGroupId]) return;

            const samplesToAdd = action.payload;
            const currentSamples = state.groups[state.activeGroupId].samples;

            // Filter out samples that already exist in the group
            const newSamples = samplesToAdd.filter((newSample) =>
                !currentSamples.some((existing) => existing.sample_id === newSample.sample_id)
            );

            // Add all new samples at once
            if (newSamples.length > 0) {
                state.groups[state.activeGroupId].samples.push(...newSamples);
            }
        },
        removeSample: (state, action: PayloadAction<{ groupId: number; sampleId: number }>) => {
            const { groupId, sampleId } = action.payload;
            if (groupId && state.groups[groupId]) {
                const samples = state.groups[groupId].samples;
                const index = samples.findIndex((sample) => sample.sample_id === sampleId);

                if (index !== -1) {
                    samples.splice(index, 1);
                } else {
                    console.error(`Sample with ID ${sampleId} not found for removal`);
                }
            }
        },
        duplicateSample: (state, action: PayloadAction<{ sampleId: number; newSampleId: number }>) => {
            if (!state.activeGroupId || !state.groups[state.activeGroupId]) return;
            // Handle the new sample_id based format
            const { sampleId, newSampleId } = action.payload;
            const samples = state.groups[state.activeGroupId].samples;

            // Find the sample by sample_id
            const sampleToDuplicate = samples.find((sample) => sample.sample_id === sampleId);

            if (sampleToDuplicate) {
                // Create duplicate with new ID
                samples.push({
                    ...sampleToDuplicate,
                    sample_id: newSampleId
                });
            } else {
                // eslint-disable-next-line no-console
                console.error(`Sample with ID ${sampleId} not found for duplication`);
            }
        },
        // Add this new reducer in the reducers object of the groupSlice
        duplicateSamplesInBatch: (state, action: PayloadAction<{ sampleId: number; newSampleId: number }[]>) => {
            if (!state.activeGroupId || !state.groups[state.activeGroupId]) return;

            const duplications = action.payload;
            const samples = state.groups[state.activeGroupId].samples;

            // Track which samples couldn't be duplicated
            const failures: number[] = [];
            let successCount = 0;

            // Process all duplications in a single operation
            duplications.forEach(({ sampleId, newSampleId }) => {
                // Find the sample by sample_id
                const sampleToDuplicate = samples.find((sample) => sample.sample_id === sampleId);

                if (sampleToDuplicate) {
                    // Create duplicate with new ID
                    samples.push({
                        ...sampleToDuplicate,
                        sample_id: newSampleId
                    });
                    successCount++;
                } else {
                    failures.push(sampleId);
                }
            });

            // Log any failures
            if (failures.length > 0) {
                // eslint-disable-next-line no-console
                console.error(`Failed to duplicate samples: ${failures.join(', ')}`);
            }
        },
        updateGroupStatus: (state, action: PayloadAction<string | null>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                state.groups[state.activeGroupId].status = action.payload;
            }
        },
        updateGroupPurpose: (state, action: PayloadAction<string>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                state.groups[state.activeGroupId].purpose = action.payload;
            }
        },
        // Update vendor handling to be similar to status
        updateGroupVendor: (state, action: PayloadAction<string | null>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                state.groups[state.activeGroupId].vendor = action.payload;
            }
        },
        // Update assay handling to be similar to status
        updateGroupAssay: (state, action: PayloadAction<string | null>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                state.groups[state.activeGroupId].assay = action.payload;
            }
        },
        updateGroupCreationDate: (state, action: PayloadAction<string>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                state.groups[state.activeGroupId].creation_date = action.payload;
            }
        },
        updateGroupEta: (state, action: PayloadAction<string | null>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                state.groups[state.activeGroupId].eta = action.payload;
            }
        },
        // Add new action to update experiment_id
        updateGroupExperimentId: (state, action: PayloadAction<string | null>) => {
            if (state.activeGroupId && state.groups[state.activeGroupId]) {
                state.groups[state.activeGroupId].experiment_id = action.payload;
            }
        },
        // Add new action to update subgroup_id for a sample
        updateSampleSubgroupId: (state, action: PayloadAction<{ index: number; subgroupId: string }>) => {
            const { index, subgroupId } = action.payload;
            if (state.activeGroupId &&
                state.groups[state.activeGroupId] &&
                state.groups[state.activeGroupId].samples[index]) {
                state.groups[state.activeGroupId].samples[index].subgroup_id = subgroupId;
            }
        },
        // New reducer for batch removal
        removeSamplesInBatch: (state, action: PayloadAction<number[]>) => {
            const sampleIdsToRemove = new Set(action.payload);

            if (!state.activeGroupId || sampleIdsToRemove.size === 0) {
                return;
            }

            const group = state.groups[state.activeGroupId];
            if (!group || !group.samples) {
                return;
            }

            group.samples = group.samples.filter(sample => !sampleIdsToRemove.has(sample.sample_id));
        },
    },
    extraReducers: (builder) => {
        // Add this handler for the new action
        builder.addCase(updateMultipleSubgroupIds, (state, action) => {
            const { indices, subgroupId } = action.payload;

            if (!state.activeGroupId) return;

            const group = state.groups[state.activeGroupId];
            if (!group || !group.samples) return;

            indices.forEach((index: number) => {
                if (index >= 0 && index < group.samples.length) {
                    group.samples[index].subgroup_id = subgroupId;
                }
            });
        });
    }
});

// Add a new selector to find what group a sample is in (if any)
export const findSampleInGroups = (
    state: { groups: GroupState },
    sampleId: number,
    excludeGroupId: string | null = null
): { groupId: string; groupName: string } | null => {
    if (!sampleId) return null;

    const groups = state.groups.groups;

    for (const [groupId, group] of Object.entries(groups)) {
        // Skip the excluded group (typically the active group)
        if (excludeGroupId && groupId === excludeGroupId) continue;

        // Check if the sample exists in this group
        if (group.samples && group.samples.some((sample) => sample.sample_id === sampleId)) {
            return {
                groupId,
                groupName: group.name
            };
        }
    }

    return null;
};

export const {
    addSample,
    addSamplesInBatch,
    removeSample,
    duplicateSample,
    duplicateSamplesInBatch,
    updateGroupStatus,
    updateGroupPurpose,
    updateGroupVendor, // Export the new vendor action
    updateGroupAssay, // Export the new assay action
    updateGroupCreationDate,
    updateGroupEta,
    updateGroupExperimentId,
    updateSampleSubgroupId,
    createNewGroup,
    setActiveGroup,
    deleteGroup,
    renameGroup,
    setGroups,
    updateCloudGroups,
    replaceGroupsFromCloud,
    replaceGroupFromCloud,
    removeSamplesInBatch,
} = groupSlice.actions;

// Modified selectors
type RootState = { groups: GroupState };
export const selectActiveGroupId = (state: RootState): number | null => state.groups.activeGroupId;
const selectGroup = (state: RootState, groupId: number): Group | undefined => state.groups.groups[groupId];

export const selectAllGroups = (state: RootState): Record<number, Group> => state.groups.groups;

export const selectActiveGroup = createSelector(
    [selectAllGroups, selectActiveGroupId],
    (allGroups, activeGroupId) => {
        return activeGroupId ? allGroups[activeGroupId] : null;
    }
);
export const selectGroupSamples = createSelector(
    [selectActiveGroup],
    (activeGroup) => {
        return activeGroup ? activeGroup.samples : [];
    }
);
export const selectGroupStatus = createSelector(
    [selectActiveGroup],
    (activeGroup) => {
        return activeGroup ? activeGroup.status : null;
    }
);
export const selectGroupPurpose = (state: RootState): string => {
    return state.groups.activeGroupId && state.groups.groups[state.groups.activeGroupId]
        ? state.groups.groups[state.groups.activeGroupId].purpose
        : "";
};

// Add normalized selectors for vendor and assay
export const selectGroupVendor = (state: RootState): string | null => {
    if (!state.groups.activeGroupId || !state.groups.groups[state.groups.activeGroupId]) return null;
    const vendor = state.groups.groups[state.groups.activeGroupId].vendor;
    return vendor;
};

export const selectGroupAssay = (state: RootState): string | null => {
    if (!state.groups.activeGroupId || !state.groups.groups[state.groups.activeGroupId]) return null;
    const assay = state.groups.groups[state.groups.activeGroupId].assay;
    return assay;
};
export const selectGroupCreationDate = (state: RootState): string => {
    return state.groups.activeGroupId && state.groups.groups[state.groups.activeGroupId]
        ? state.groups.groups[state.groups.activeGroupId].creation_date
        : new Date().toISOString().slice(0, 10);
};
export const selectGroupEta = (state: RootState): string | null => {
    return state.groups.activeGroupId && state.groups.groups[state.groups.activeGroupId]
        ? state.groups.groups[state.groups.activeGroupId].eta
        : null;
};
export const selectGroupExperimentId = (state: RootState): string | null => {
    return state.groups.activeGroupId && state.groups.groups[state.groups.activeGroupId]
        ? state.groups.groups[state.groups.activeGroupId].experiment_id || null
        : null;
};

// Add new selectors for cloud groups
export const selectCloudGroups = (state: RootState): Record<number, CloudGroup> => state.groups.cloudGroups;

export const selectUnsyncedGroups = createSelector(
    [
        (state: RootState) => state.groups.groups,
        (state: RootState) => state.groups.cloudGroups
    ],
    (localGroups, cloudGroups) => {
        const unsyncedGroups: number[] = [];

        // Check for modified local groups
        Object.values(localGroups).forEach((group) => {
            // Skip if group name is not set
            if (!group.id) return;

            // Check if this group exists in the cloud
            const cloudGroup = cloudGroups[group.id];

            // If the group doesn't exist in the cloud, it needs to be pushed
            if (!cloudGroup) {
                unsyncedGroups.push(group.id);
                return;
            }

            // Compare samples - normalize arrays for comparison
            const localSamples = JSON.stringify(
                (group.samples || []).map((s: Sample) => ({
                    parent_sample_id: s.parent_sample_id,
                    sample_id: s.sample_id,
                    subgroup_id: s.subgroup_id
                }))
            );
            const cloudSamples = JSON.stringify(
                (cloudGroup.samples || []).map((s: Sample) => ({
                    parent_sample_id: s.parent_sample_id,
                    sample_id: s.sample_id,
                    subgroup_id: s.subgroup_id
                }))
            );

            // Normalize status for comparison
            const localStatus = typeof group.status === 'object' ? (group.status as any)?.value : group.status;
            const cloudStatus = typeof cloudGroup.status === 'object' ? (cloudGroup.status as any)?.value : cloudGroup.status;

            // Add normalization for vendor and assay
            const localVendor = typeof group.vendor === 'object' ? (group.vendor as any)?.value : group.vendor;
            const cloudVendor = typeof cloudGroup.vendor === 'object' ? (cloudGroup.vendor as any)?.value : cloudGroup.vendor;

            const localAssay = typeof group.assay === 'object' ? (group.assay as any)?.value : group.assay;
            const cloudAssay = typeof cloudGroup.assay === 'object' ? (cloudGroup.assay as any)?.value : cloudGroup.assay;

            // Compare normalized values including experiment_id
            if (localSamples !== cloudSamples ||
                localStatus !== cloudStatus ||
                group.purpose !== cloudGroup.purpose ||
                localVendor !== cloudVendor ||
                localAssay !== cloudAssay ||
                group.creation_date !== cloudGroup.creation_date ||
                group.eta !== cloudGroup.eta ||
                group.experiment_id !== cloudGroup.experiment_id ||
                group.name !== cloudGroup.name
            ) {
                unsyncedGroups.push(group.id);
            }
        });

        // Check for groups that exist in cloud but not locally (deleted groups)
        Object.keys(cloudGroups).forEach((cloudGroupId) => {
            // If a cloud group doesn't exist locally, it was deleted and needs to be synced
            if (!Object.values(localGroups).some((group) => group.id === Number(cloudGroupId))) {
                unsyncedGroups.push(Number(cloudGroupId));
            }
        });

        return unsyncedGroups;
    });

// Add a new selector to check if a sample is in a specific group
const checkSampleInGroup = (state: RootState, sampleId: number, groupId: number): boolean => {
    return state.groups.groups[groupId]?.samples?.some((sample) => sample.sample_id === sampleId) || false;
};

// Add new selectors to get unique vendors and assays from all groups
export const selectUniqueVendors = (state: RootState): { value: string; label: string }[] => {
    const groups = state.groups.groups;
    // Collect all non-null and non-undefined vendor values
    const vendorValues = Object.values(groups)
        .map((group) => {
            const vendor = group.vendor;
            if (vendor === null || vendor === undefined) return null;
            return typeof vendor === 'object' ? (vendor as any).value : vendor;
        })
        .filter(Boolean) as string[]; // Filter out null/undefined values

    // Create a Set to get unique values and convert back to array
    const uniqueVendors = Array.from(new Set(vendorValues));

    // Format as options for select input
    return uniqueVendors.map((vendor) => ({
        value: vendor,
        label: vendor
    }));
};

export const selectUniqueAssays = (state: RootState): { value: string; label: string }[] => {
    const groups = state.groups.groups;
    // Collect all non-null and non-undefined assay values
    const assayValues = Object.values(groups)
        .map((group) => {
            const assay = group.assay;
            if (assay === null || assay === undefined) return null;
            return typeof assay === 'object' ? (assay as any).value : assay;
        })
        .filter(Boolean) as string[]; // Filter out null/undefined values

    // Create a Set to get unique values and convert back to array
    const uniqueAssays = Array.from(new Set(assayValues));

    // Format as options for select input
    return uniqueAssays.map((assay) => ({
        value: assay,
        label: assay
    }));
};

export const selectGroupSampleIdsSignature = createSelector(
    [(state: RootState) => {
        // Create a more comprehensive signature that includes sample-to-group mappings
        const sampleToGroupMappings: string[] = [];
        Object.values(state.groups.groups).forEach((group) => {
            group.samples?.forEach((sample) => {
                if (sample.sample_id) {
                    sampleToGroupMappings.push(`${sample.sample_id}:${group.name}`);
                }
            });
        });
        return sampleToGroupMappings.sort().join('|');
    }],
    (signature) => signature
);

export default groupSlice.reducer;

